import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useData } from "../data/DataProvider";
import { useStaticText } from "../staticText/staticTextProvider";
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { HOST, PRICE_CATEGORY } from "../setting";
import { useFirebaseToken } from "../firebase/FirebaseProvider";
import { useBIService } from "../bi/BIServiceProvider";
import { useAnalytics } from "../analytics/AnalyticsProvider";


type VocabResult = {
    estimated: number
    nextGoal: number
    nextGoalDate: Date
}

type GrammarResult = {
    currentLevel: string
    nextLevel: string
    correctAnswers: number
    totalQuestions: number
    nextGoalDate: Date
}


const NAVIGATE_TIMEOUT = 300


export type Step = "start" | "gender" | "age" | "language" | "video" | "how" | "learningEfficiencyFocus" | "why" | "name" | "personalPlan" |
    "level" | "goodHands1" | "struggles" | "struggleView" | "learningEfficiencyRegularStudy" |"topics" | "goodHands2" | "goal" | "askTest" |
    "vocabularyTestBegginer" | "vocabularyTestIntermediate" | "vocabularyTestAdvanced" | "testResults" | "grammarTest" | "grammarTestResults" |
    "lexTest" | "lexTestResults" | "creatProgram" | "email" | "expect1Week" | "expect4Week" | "expect12Week" | "expectMonth12" | "summary" | "levelUp" | "choosePlan"

type ButtonState = "active" | "disabled" | "gone"

type GlobalStateContextValue = {
    language: number | null
    setLanguage: (language: number) => void
    topics: number[]
    chooseTopic: (topics: number) => void
    currentStep: Step
    currentTitle: string | null
    showLogo: boolean
    showProgressBar: boolean
    progressBarValue: number
    buttonState: ButtonState
    name: string
    setName: (name: string) => void
    email: string
    setEmail: (email: string) => void
    isDarkView: boolean
    why: number | null
    setWhy: (why: number) => void
    hows: number[]
    chooseHow: (how: number) => void
    level: number | null
    setLevel: (level: number) => void
    struggles: number[]
    chooseStruggle: (struggle: number) => void
    goal: number | null
    setGoal: (goal: number) => void
    vocabulary: number[]
    chooseVocabulary: (vocabulary: number) => void
    skipVocabTest: () => void
    vocabResult: VocabResult | null
    grammarResult: GrammarResult | null
    grammarAnswers: { [questionId: number]: string }
    chooseGrammarAnswer: (questionId: number, answerId: string) => void
    lexResults: { [questionId: number]: boolean }
    recordLexResult: (questionId: number, isCorrect: boolean) => void
    setProgressBarValue: (value: number) => void
    setButtonActive: () => void
    setButtonDisabled: () => void
    finishOnboarding: () => void
    fromEmail: boolean
    gender: 'male' | 'female' | null
    setGender: (gender: 'male' | 'female') => void
    age: string | null
    setAge: (age: string) => void
    stratedFromLanguage: boolean
    speakingHarderThenUnderstand: boolean | null
    setSpeakingHarderThenUnderstand: (value: boolean) => void
    getResultOfVocabTest: () => VocabResult | null
    STEPS: Step[]
    toggleTitle: (hide: boolean) => void,
    navigateToNextStep: (fromStep?: Step) => void,
    isLangFromUserAgent: boolean
};


const GlobalStateContext = createContext<GlobalStateContextValue>({
    language: null,
    setLanguage: (language: number) => { },
    topics: [],
    chooseTopic: (topics: number) => { },
    currentStep: "language",
    currentTitle: null,
    showLogo: false,
    showProgressBar: false,
    progressBarValue: 0.0,
    buttonState: "disabled",
    name: "",
    setName: (name: string) => { },
    email: "",
    setEmail: (email: string) => { },
    isDarkView: false,
    why: null,
    setWhy: (why: number) => { },
    hows: [],
    chooseHow: (how: number) => { },
    level: null,
    setLevel: (level: number) => { },
    struggles: [],
    chooseStruggle: (struggle: number) => { },
    goal: null,
    setGoal: (goal: number) => { },
    vocabulary: [],
    chooseVocabulary: (vocabulary: number) => { },
    skipVocabTest: () => { },
    vocabResult: null,
    grammarResult: null,
    grammarAnswers: {},
    chooseGrammarAnswer: (questionId: number, answerId: string) => { },
    lexResults: {},
    recordLexResult: (questionId: number, isCorrect: boolean) => { },
    setProgressBarValue: (value: number) => { },
    setButtonActive: () => { },
    setButtonDisabled: () => { },
    finishOnboarding: () => { },
    fromEmail: false,
    gender: null,
    setGender: () => { },
    age: null,
    setAge: () => { },
    stratedFromLanguage: false,
    speakingHarderThenUnderstand: null,
    setSpeakingHarderThenUnderstand: () => { },
    getResultOfVocabTest: () => null,
    STEPS: [],
    toggleTitle: () => {},
    navigateToNextStep: () => {},
    isLangFromUserAgent: false
});


export const GloabalStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [gender, setGender] = useState<'male' | 'female' | null>(null)
    const [age, setAge] = useState<string | null>(null)
    const [language, setLanguage] = useState<number | null>(null)
    const [topics, setTopics] = useState<number[]>([])
    const [currentStep, setCurrentStep] = useState<Step>("gender")
    const [currentTitle, setCurrentTitle] = useState<string | null>(null)
    const [showLogo, setShowLogo] = useState<boolean>(false)
    const [showProgressBar, setShowProgressBar] = useState<boolean>(false)
    const [progressBarValue, setProgressBarValue] = useState<number>(0.0)
    const [buttonState, setButtonState] = useState<ButtonState>("disabled")
    const [name, setName] = useState<string>("")
    const [isDarkView, setIsDarkView] = useState<boolean>(false)
    const [why, setWhy] = useState<number | null>(null)
    const [hows, setHows] = useState<number[]>([])
    const [level, setLevel] = useState<number | null>(null)
    const [struggles, setStruggles] = useState<number[]>([])
    const [goal, setGoal] = useState<number | null>(null)
    const [vocabulary, setVocabulary] = useState<number[]>([])
    const [email, setEmail] = useState<string>("")
    const [vocabResult, setVocabResult] = useState<VocabResult | null>(null)
    const [grammarResult, setGrammarResult] = useState<GrammarResult | null>(null)
    const [grammarAnswers, setGrammarAnswers] = useState<{ [questionId: number]: string }>({})
    const [lexResults, setLexResults] = useState<{ [questionId: number]: boolean }>({})
    const { setLanguageCode, getStaticText, languageCode } = useStaticText()
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const location = useLocation();
    const { data, levels, goals } = useData()
    const { token } = useFirebaseToken()
    const { email: sendEmailToBI } = useBIService()
    const { track } = useAnalytics()
    const [testWasSkipped, setTestWasSkipped] = useState<boolean>(false)
    const [searchParams, _] = useSearchParams();
    const [isLangFromUserAgent, setIsLangFromUserAgent] = useState(false)
    const [fromEmail, setFromEmail] = useState<boolean>(false)
    const [speakingHarderThenUnderstand, setSpeakingHarderThenUnderstand] = useState<boolean | null>(null)
    const stratedFromLanguage = false;
    const [stateIsReady, setStateIsReady] = useState(false)
    const [showTitle, setShowTitle] = useState(true)
    const emailSentRef = useRef(false)


    const getLexTestIsNeededFlag = (): boolean => {
        let storedVariant = localStorage.getItem('lexTestIsNeeded');
        if (storedVariant === null) {
            return true
        }
        return Boolean(Number(storedVariant))
    }

    const showLexTest = getLexTestIsNeededFlag()

    // A/B test: "short" or "long" flow
    const FLOW_VARIANT_KEY = 'ab_flow_variant';

    const [flowVariant] = useState<"short" | "long">(() => {
        // const storedVariant = localStorage.getItem(FLOW_VARIANT_KEY);

        // if (storedVariant === "short" || storedVariant === "long") {
        //     return storedVariant;
        // }

        // // Randomly assign variant (50/50)
        // const randomVariant: "short" | "long" = Math.random() < 0.5 ? "short" : "long";
        // localStorage.setItem(FLOW_VARIANT_KEY, randomVariant);
        // track("Flow Variant Selected", { variant: randomVariant });
        // return randomVariant;
        return "long";
    });

    const STEPS_LONG: Step[] = [
        "start", "language", "video", "name", "personalPlan",
        "level", "how", "learningEfficiencyFocus", "why", "goodHands1", "struggles", "struggleView", "learningEfficiencyRegularStudy", "topics", "goodHands2", "goal", "askTest",
        "vocabularyTestBegginer", "vocabularyTestIntermediate", "vocabularyTestAdvanced", "testResults", "grammarTest", "grammarTestResults",
        "creatProgram", "email", "summary", "expect1Week", "expect4Week", "expect12Week", "expectMonth12", "levelUp", "choosePlan"
    ]

    const STEPS_SHORT: Step[] = [
        "start", "goodHands1", "language", "personalPlan", "level", "learningEfficiencyFocus",
        "how", "learningEfficiencyRegularStudy", "topics", "goodHands2", "goal", "askTest",
        "vocabularyTestBegginer", "vocabularyTestIntermediate", "vocabularyTestAdvanced", "testResults", "grammarTest", "grammarTestResults",
        "creatProgram", "email", "summary", "expect1Week", "expect4Week", "expect12Week", "expectMonth12", "levelUp", "choosePlan"
    ]

    let STEPS: Step[] = flowVariant === "short" ? [...STEPS_SHORT] : [...STEPS_LONG]

    if (showLexTest) {
        const idx = STEPS.indexOf("grammarTestResults")
        if (idx > -1) {
            STEPS.splice(idx + 1, 0, "lexTest")
            STEPS.splice(idx + 2, 0, "lexTestResults")
        }
    }

    function setDefaultLanguage() {
        const _language = localStorage.getItem("language")
        if (_language) {
            setLanguage(Number(_language))
        } else {
            const lang_iso = navigator.language.split('-')[0]
            const lang = data?.languages.find(x => x.iso.toLocaleLowerCase() === lang_iso.toLocaleLowerCase())
            if (lang) {
                setLanguage(lang.id)
                setIsLangFromUserAgent(true)
            }
        }
    }


    function loadState() {
        setDefaultLanguage()
        const _gender = localStorage.getItem("gender")
        if (_gender) {
            setGender(_gender as 'male' | 'female')
        }
        const _age = localStorage.getItem("age")
        if (_age) {
            setAge(_age)
        }
        const _name = localStorage.getItem("name")
        if (_name) {
            setName(_name)
        }
        const _level = localStorage.getItem("level")
        if (_level) {
            setLevel(Number(_level))
        }
        const _hows = localStorage.getItem("hows")
        if (_hows) {
            setHows(JSON.parse(_hows))
        }
        const _why = localStorage.getItem("why")
        if (_why) {
            setWhy(Number(_why))
        }

        const _struggle = localStorage.getItem("struggles")
        if (_struggle) {
            if (typeof (JSON.parse(_struggle)) === "number") {
                localStorage.removeItem("struggles")
            } else {
                setStruggles(JSON.parse(_struggle))
            }

        }

        const _topics = localStorage.getItem("topics")
        if (_topics) {
            setTopics(JSON.parse(_topics))
        }
        const _goal = localStorage.getItem("goal")
        if (_goal) {
            setGoal(Number(_goal))
        }
        const _vocabulary = localStorage.getItem("vocabulary")
        if (_vocabulary) {
            setVocabulary(JSON.parse(_vocabulary))
        }
        const _grammarAnswers = localStorage.getItem("grammarAnswers")
        if (_grammarAnswers) {
            setGrammarAnswers(JSON.parse(_grammarAnswers))
        }
        const _email = localStorage.getItem("email")
        if (_email) {
            setEmail(_email)
        }
        const _vocabResult = localStorage.getItem("vocabResult")
        if (_vocabResult) {
            const parsed = JSON.parse(_vocabResult)
            // Convert nextGoalDate string back to Date object
            if (parsed.nextGoalDate) {
                parsed.nextGoalDate = new Date(parsed.nextGoalDate)
            }
            setVocabResult(parsed)
        }
        const _grammarResult = localStorage.getItem("grammarResult")
        if (_grammarResult) {
            const parsed = JSON.parse(_grammarResult)
            // Convert nextGoalDate string back to Date object
            if (parsed.nextGoalDate) {
                parsed.nextGoalDate = new Date(parsed.nextGoalDate)
            }
            setGrammarResult(parsed)
        }
        const _speakingHarderThenUnderstand = localStorage.getItem("speakingHarderThenUnderstand")
        if (_speakingHarderThenUnderstand !== null) {
            setSpeakingHarderThenUnderstand(_speakingHarderThenUnderstand === "true")
        }

    }

    useEffect(() => {
        loadState()
        detectEmail()
        setStateIsReady(true)

        const ttclid = searchParams.get("ttclid")
        if (ttclid) {
            localStorage.setItem("ttclid", ttclid)
        }
    }, [])

    useEffect(() => {
        if (data && !language) {
            setDefaultLanguage()
        }
    }, [data])

    function handleStepEnter(_step: Step) {
        if (_step === "grammarTest") {
            // Clear answer for current grammar question only
            const urlMatch = location.pathname.match(/^\/grammarTest\/(\d+)$/);
            if (urlMatch) {
                const questionIndex = parseInt(urlMatch[1], 10);
                const grammarTestData = data?.grammar_test;
                if (grammarTestData && grammarTestData.questions[questionIndex]) {
                    const currentQuestion = grammarTestData.questions[questionIndex];
                    if (grammarAnswers[currentQuestion.id]) {
                        const updatedAnswers = { ...grammarAnswers };
                        delete updatedAnswers[currentQuestion.id];
                        setGrammarAnswers(updatedAnswers);
                        localStorage.setItem("grammarAnswers", JSON.stringify(updatedAnswers));
                    }
                }
            }
        }
    }


    const detectEmail = () => {
        try {
            const _email = searchParams.get("EMAIL") || searchParams.get("email")
            const _languageID = Number(searchParams.get("LANG_ID") || searchParams.get("lang_id"))
            if (!location.pathname.includes("choosePlan") || !_email || !_languageID) {
                return
            }
            setEmail(_email)
            localStorage.setItem("email", `${_email}`)
            setLanguage(_languageID)
            localStorage.setItem("language", `${_languageID}`)
            setCurrentStep("choosePlan")
            setLanguageCode(data!.languages.find(x => x.id === _languageID)!.iso)
            setFromEmail(true)
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        let step = location.pathname.replace("/", "")
        if (step.endsWith("/")) {
            step = step.slice(0, -1)
        }
        try {
            if (step as Step === "testResults") {
                const result = getResultOfVocabTest()
                setVocabResult(result)
                if (result) {
                    localStorage.setItem("vocabResult", JSON.stringify(result))
                } else {
                    navigate("/grammarTest/0")
                }
            }
            if (step as Step === "grammarTestResults") {
                const result = getResultOfGrammarTest()
                setGrammarResult(result)
                if (result) {
                    localStorage.setItem("grammarResult", JSON.stringify(result))
                } else {
                    navigate("/creatProgram")
                }
            }
            if (step as Step === "level") {
                setLevel(null)
            }
            if (step as Step === "why") {
                setWhy(null)
            }
            if (step.startsWith("struggleView")) {
                step = "struggleView"
            }
            if (step.startsWith("grammarTest/")) {
                step = "grammarTest"
            }
            if (step.startsWith("lexTest/")) {
                step = "lexTest"
            }
            setCurrentStep(step as Step)
            handleStepEnter(step as Step)
            if (step === "choosePlan" && email && !emailSentRef.current) {
                emailSentRef.current = true
                sendEmail()
            }
        } catch (error) {
            console.error(error)
        }
    }, [location, navigationType, email])

    useEffect(() => {
        if (currentStep === "start") {
            setButtonState("active")
            setCurrentTitle(getStaticText("web.unlock.english.potential"))
            setShowLogo(true)
            setShowProgressBar(false)
            setProgressBarValue(0.0)
            setIsDarkView(false)
        } if (currentStep === "gender") {
            setButtonState("gone")
            setCurrentTitle(getStaticText("web.unlock.english.potential"))
            setShowLogo(true)
            setShowProgressBar(false)
            setProgressBarValue(0.0)
            setIsDarkView(false)
        } else if (currentStep === "age") {
            setButtonState("gone")
            setCurrentTitle(getStaticText("web.unlock.english.potential"))
            setShowLogo(true)
            setShowProgressBar(false)
            setProgressBarValue(0.0)
            setIsDarkView(false)
        } else if (currentStep === "language") {
            setButtonState("disabled")
            setCurrentTitle(getStaticText("screen.language.select.title"))
            setShowLogo(true)
            setShowProgressBar(false)
            setProgressBarValue(0.1)
            setIsDarkView(false)
        } else if (currentStep === "video") {
            setButtonState('active')
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.25)
            setIsDarkView(false)
        } else if (currentStep === "name") {
            setButtonState("disabled")
            setCurrentTitle(getStaticText("onboarding.fill.name.header"))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.3)
            setIsDarkView(false)
        } else if (currentStep === "personalPlan") {
            setButtonState("disabled")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(false)
            setProgressBarValue(0.3)
            setIsDarkView(true)
        } else if (currentStep === "level") {
            setButtonState("gone")
            setCurrentTitle(getStaticText("web.how.well.do.you.know.english"))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.35)
            setIsDarkView(false)
        } else if (currentStep === "how") {
            setButtonState(hows.length > 0 ? "active" : "disabled")
            setCurrentTitle(getStaticText("onboarding.source.list.header"))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.4)
            setIsDarkView(false)
        } else if (currentStep === "learningEfficiencyFocus") {
            setButtonState("active")
            setCurrentTitle(getStaticText('web.brain.exercises'))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.4)
            setIsDarkView(false)
        }else if (currentStep === "why") {
            setButtonState("gone")
            setCurrentTitle(getStaticText("onboarding.reason.list.header"))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.45)
            setIsDarkView(false)
        } else if (currentStep === "goodHands1" || currentStep === "goodHands2") {
            setButtonState("active")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(false)
            setProgressBarValue(0.45)
            setIsDarkView(true)
        } else if (currentStep === "struggles") {
            setButtonState(struggles.length > 0 ? "active" : "disabled")
            setCurrentTitle(getStaticText("onboarding.summary.struggles.header"))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.5)
            setIsDarkView(false)
        } else if (currentStep === "struggleView") {
            setButtonState("active")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(false)
            setProgressBarValue(0.5)
            setIsDarkView(true)
        } else if (currentStep === "learningEfficiencyRegularStudy") {
            setButtonState("active")
            setCurrentTitle(getStaticText("web.brain.learns"))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.5)
            setIsDarkView(false)
        }else if (currentStep === "topics") {
            setButtonState(topics.length > 0 ? "active" : "disabled")
            setCurrentTitle(getStaticText("onboarding.topic.list.header"))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.6)
            setIsDarkView(false)
        } else if (currentStep === "goal") {
            setButtonState("disabled")
            setCurrentTitle(getStaticText("practice.goal.title"))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.70)
            setIsDarkView(false)
        } else if (currentStep === "askTest") {
            setButtonState("active")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(false)
            setProgressBarValue(0.70)
            setIsDarkView(true)
        } else if (currentStep === "vocabularyTestBegginer") {
            setButtonState("active")
            setCurrentTitle(getStaticText("web.select.words.you.know"))
            setShowLogo(false)
            setShowProgressBar(true)
            // Calculate total steps dynamically: 3 vocab + 1 result + grammar questions
            const grammarQuestionCount = data?.grammar_test?.questions?.length || 0;
            const totalTestSteps = 3 + 1 + grammarQuestionCount; // vocab + result + grammar
            setProgressBarValue(0.75 + (0.15 / totalTestSteps) * 1) // Step 1
            setIsDarkView(false)
        } else if (currentStep === "vocabularyTestIntermediate") {
            setButtonState("active")
            setCurrentTitle(getStaticText("web.select.words.you.know"))
            setShowLogo(false)
            setShowProgressBar(true)
            // Calculate total steps dynamically: 3 vocab + 1 result + grammar questions
            const grammarQuestionCount = data?.grammar_test?.questions?.length || 0;
            const totalTestSteps = 3 + 1 + grammarQuestionCount; // vocab + result + grammar
            setProgressBarValue(0.75 + (0.15 / totalTestSteps) * 2) // Step 2
            setIsDarkView(false)
        } else if (currentStep === "vocabularyTestAdvanced") {
            setButtonState("active")
            setCurrentTitle(getStaticText("web.select.words.you.know"))
            setShowLogo(false)
            setShowProgressBar(true)
            // Calculate total steps dynamically: 3 vocab + 1 result + grammar questions
            const grammarQuestionCount = data?.grammar_test?.questions?.length || 0;
            const totalTestSteps = 3 + 1 + grammarQuestionCount; // vocab + result + grammar
            setProgressBarValue(0.75 + (0.15 / totalTestSteps) * 3) // Step 3
            setIsDarkView(false)
        } else if (currentStep === "grammarTest") {
            setButtonState("gone")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(true)
            // Calculate progress based on current question from URL
            const urlMatch = location.pathname.match(/^\/grammarTest\/(\d+)$/);
            const questionIndex = urlMatch ? parseInt(urlMatch[1], 10) : 0;
            const grammarTestData = data?.grammar_test;
            if (grammarTestData && grammarTestData.questions.length > 0) {
                // Calculate total steps dynamically: 3 vocab + 1 result + grammar questions
                const totalTestSteps = 3 + 1 + grammarTestData.questions.length;
                const stepNumber = 4 + questionIndex + 1; // After vocab(3) + result(1) + current question
                const currentProgress = 0.75 + (0.15 / totalTestSteps) * stepNumber;
                setProgressBarValue(currentProgress);
            } else {
                setProgressBarValue(0.75 + (0.15 / 4) * 4); // Default fallback
            }
            setIsDarkView(true)
        } else if (currentStep === "testResults") {
            setButtonState("disabled")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(false)
            // Calculate total steps dynamically: 3 vocab + 1 result + grammar questions
            const grammarQuestionCount = data?.grammar_test?.questions?.length || 0;
            const totalTestSteps = 3 + 1 + grammarQuestionCount; // vocab + result + grammar
            setIsDarkView(true)
        } else if (currentStep === "grammarTestResults") {
            setButtonState("disabled")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(false)
            setProgressBarValue(0.90) // After grammar test complete
            setIsDarkView(true)
        } else if (currentStep === "lexTest") {
            setButtonState("gone")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.94)
            const lexQuestionCount = data?.lex_test?.questions?.length || 0;
            setIsDarkView(true)
        } else if (currentStep === "lexTestResults") {
            setButtonState("disabled")
            setCurrentTitle(getStaticText("web.test.lexical.access.score"))
            setShowLogo(false)
            setShowProgressBar(false)
            setProgressBarValue(0.95) // After all tests complete
            setIsDarkView(true)
        } else if (currentStep === "creatProgram") {
            setButtonState("disabled")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(false)
            setProgressBarValue(0.90)
            setIsDarkView(true)
        } else if (currentStep === "email") {
            setButtonState("disabled")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(false)
            setProgressBarValue(0.90)
            setIsDarkView(true)
        } else if (currentStep === "expect1Week" || currentStep === "expect4Week" || currentStep === "expect12Week" || currentStep === "expectMonth12") {
            setButtonState("active")
            setCurrentTitle(null)
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(currentStep === "expect1Week" ? 0.93 : currentStep === "expect4Week" ? 0.94 : currentStep === "expect12Week" ? 0.95 : 0.97)
            setIsDarkView(true)
        } else if (currentStep === "summary") {
            setButtonState("disabled")
            setCurrentTitle(getStaticText("web.final.touches"))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(0.92)
            setIsDarkView(true)
        } else if (currentStep === "levelUp") {
            setButtonState("active")
            setCurrentTitle(getStaticText("web.level.up.english"))
            setShowLogo(false)
            setShowProgressBar(true)
            setProgressBarValue(1)
            setIsDarkView(true)
        } else if (currentStep === "choosePlan") {
            setButtonState("gone")
            setCurrentTitle(getStaticText("web.grab.personal.plan"))
            setShowLogo(false)
            setShowProgressBar(false)
            setProgressBarValue(1)
            setIsDarkView(false)
        }
        if (language === -1) {
            setLanguage(data?.languages.find(x => x.iso === "en")?.id ?? -1)
        }
    }, [currentStep, languageCode])

    useEffect(() => {
        if (!data?.languages.length) {
            return
        }
        if (language) {
            setLanguageCode(data!.languages.find(x => x.id === language)!.iso)
            localStorage.setItem("language", `${language}`)
        }
        if (language && currentStep === "language" && buttonState !== "active") {
            setButtonState("active")
        }
    }, [language, currentStep, buttonState, data])

    useEffect(() => {
        if (name.length > 1 && currentStep === "name" && buttonState !== "active") {
            setButtonState("active")
        }
        localStorage.setItem("name", name)
    }, [name, currentStep, buttonState])

    useEffect(() => {
        if (currentStep === "email") {
            const validateEmail = (email: string) => {
                const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return regex.test(email);
            };
            setButtonState(validateEmail(email) ? "active" : "disabled")
            if (validateEmail(email) || email === "") {
                localStorage.setItem("email", email)
            }
        }

    }, [email, currentStep])

    useEffect(() => {
        if (level && currentStep === "level") {
            localStorage.setItem("level", `${level}`)
            setTimeout(() => navigateToNextStep("level"), NAVIGATE_TIMEOUT)
        }
    }, [level, currentStep])

    useEffect(() => {
        if (hows.length && currentStep === "how") {
            if (buttonState !== "active") {
                setButtonState("active")
            }
            localStorage.setItem("hows", JSON.stringify(hows))
        }
    }, [hows, currentStep, buttonState])

    useEffect(() => {
        if (why && currentStep === "why") {
            localStorage.setItem("why", `${why}`)
            setTimeout(() => navigateToNextStep("why"), NAVIGATE_TIMEOUT)
        }
    }, [why, currentStep])

    useEffect(() => {
        if (struggles.length && currentStep === "struggles" && buttonState !== "active") {
            setButtonState("active")
        }
    }, [struggles, currentStep])

    useEffect(() => {
        if (topics.length && currentStep === "topics" && buttonState !== "active") {
            setButtonState("active")
        }
    }, [topics])

    useEffect(() => {
        if (goal && currentStep === "goal" && buttonState !== "active") {
            localStorage.setItem("goal", `${goal}`)
            setButtonState("active")
        }
    }, [goal, currentStep, buttonState])


    function chooseHow(how: number) {
        let _hows = []
        if (hows.includes(how)) {
            _hows = hows.filter(i => i !== how)
        } else {
            _hows = [...hows, how]
        }
        if (_hows.length === 0) {
            setButtonState("disabled")
        }
        setHows(_hows)
        localStorage.setItem("hows", JSON.stringify(_hows))
    }

    function chooseTopic(topic: number) {
        let _topics = []
        if (topics.includes(topic)) {
            _topics = topics.filter(i => i !== topic)
        } else {
            _topics = [...topics, topic]
        }
        if (_topics.length === 0) {
            setButtonState("disabled")
        }
        setTopics(_topics)
        localStorage.setItem("topics", JSON.stringify(_topics))
    }

    function chooseStruggle(struggle: number) {
        let _struggles = []
        if (struggles.includes(struggle)) {
            _struggles = struggles.filter(i => i !== struggle)
        } else {
            _struggles = [...struggles, struggle]
        }
        if (_struggles.length === 0) {
            setButtonState("disabled")
        }
        setStruggles(_struggles)
        localStorage.setItem("struggles", JSON.stringify(_struggles))
    }

    function chooseVocabulary(word: number) {
        let _vocabulary = []
        if (vocabulary.includes(word)) {
            _vocabulary = vocabulary.filter(i => i !== word)
        } else {
            _vocabulary = [...vocabulary, word]
        }
        setVocabulary(_vocabulary)
        localStorage.setItem("vocabulary", JSON.stringify(_vocabulary))
    }

    function chooseGrammarAnswer(questionId: number, answerId: string) {
        const _grammarAnswers = {
            ...grammarAnswers,
            [questionId]: answerId
        }
        setGrammarAnswers(_grammarAnswers)
        localStorage.setItem("grammarAnswers", JSON.stringify(_grammarAnswers))
    }

    function recordLexResult(questionId: number, isCorrect: boolean) {
        const _lexResults = {
            ...lexResults,
            [questionId]: isCorrect
        }
        setLexResults(_lexResults)
    }

    function skipVocabTest() {
        setTestWasSkipped(true)
        navigate("/creatProgram")
    }

    function navigateToNextStep(fromStep?: Step) {
        const step = fromStep ?? currentStep
        const currentIdx = STEPS.indexOf(step)
        if (currentIdx === -1 || currentIdx >= STEPS.length - 1) {
            return
        }
        const nextStep = STEPS[currentIdx + 1]
        // Handle special cases for multi-question steps
        if (nextStep === "grammarTest") {
            navigate("/grammarTest/0")
        } else if (nextStep === "lexTest") {
            navigate("/lexTest/0")
        } else {
            navigate(`/${nextStep}`)
        }
    }


    function getResultOfGrammarTest(): GrammarResult | null {
        const grammarTest = data?.grammar_test;
        if (!grammarTest || !grammarTest.questions.length) return null;

        let correctAnswers = 0;
        grammarTest.questions.forEach(question => {
            const userAnswer = grammarAnswers[question.id];
            if (userAnswer === question.correct_answer) {
                correctAnswers++;
            }
        });

        const totalQuestions = grammarTest.questions.length;
        let currentLevel = "A1";
        let nextLevel = "A2";

        // Interpreting Results logic
        if (correctAnswers >= 0 && correctAnswers <= 4) {
            currentLevel = "A1";
            nextLevel = "A2";
        } else if (correctAnswers === 5 || correctAnswers === 6) {
            currentLevel = "A2";
            nextLevel = "B1";
        } else if (correctAnswers === 7) {
            currentLevel = "B1";
            nextLevel = "B2";
        } else if (correctAnswers >= 8 && correctAnswers <= 10) {
            currentLevel = "B2";
            nextLevel = "C1";
        } else if (correctAnswers >= 11) {
            currentLevel = "C1";
            nextLevel = "C1+";
        }

        const now = new Date();
        const nextDate = new Date(now);
        nextDate.setMonth(now.getMonth() + 1);

        return {
            currentLevel,
            nextLevel,
            correctAnswers,
            totalQuestions,
            nextGoalDate: nextDate
        };
    }

    function getResultOfVocabTest(): VocabResult | null {
        const advancedWordsIds = data!.test_words.advanced.map((x) => x.id)
        const now = new Date();
        const nextDate = new Date(now);
        nextDate.setMonth(now.getMonth() + 1);
        const advancedCount = vocabulary.filter((x) => advancedWordsIds.includes(x)).length
        if (advancedCount >= 6) {
            return {
                estimated: 6000,
                nextGoal: 6250,
                nextGoalDate: nextDate
            }
        }
        if (advancedCount >= 4) {
            return {
                estimated: 5000,
                nextGoal: 5250,
                nextGoalDate: nextDate
            }
        }
        if (advancedCount >= 2) {
            return {
                estimated: 4000,
                nextGoal: 4250,
                nextGoalDate: nextDate
            }
        }

        const intermediateWordsIds = data!.test_words.intermediate.map((x) => x.id)
        const intermediateCount = vocabulary.filter((x) => intermediateWordsIds.includes(x)).length
        if (advancedCount === 1 || intermediateCount >= 4) {
            return {
                estimated: 3500,
                nextGoal: 3750,
                nextGoalDate: nextDate
            }
        }
        if (intermediateCount >= 2) {
            return {
                estimated: 2500,
                nextGoal: 2750,
                nextGoalDate: nextDate
            }
        }


        const beginnerWordsIds = data!.test_words.beginner.map((x) => x.id)
        const beginnerCount = vocabulary.filter((x) => beginnerWordsIds.includes(x)).length
        if (intermediateCount === 1 || beginnerCount >= 4) {
            return {
                estimated: 1500,
                nextGoal: 1600,
                nextGoalDate: nextDate
            }
        }

        if (beginnerCount >= 2) {
            return {
                estimated: 500,
                nextGoal: 600,
                nextGoalDate: nextDate
            }
        }

        return null
    }

    function sendEmail() {
        try {
            // Отправляем email в BI сервис
            sendEmailToBI(email)

            fetch(`${HOST}/api/v1/web-onboarding-email`, {
                method: 'POST',
                body: JSON.stringify({
                    "name": name,
                    "email": email,
                    "language": language,
                    "source": PRICE_CATEGORY
                }),
                credentials: "include",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
            })
        } catch (error) {
            console.error(error)
        }
    }

    function sendOnboardingData() {
        const allWords = [...data!.test_words!.beginner, ...data!.test_words!.intermediate, ...data!.test_words!.advanced]
        fetch(`${HOST}/api/v1/web-onboarding-finish`, {
            method: 'POST',
            body: JSON.stringify({
                "gender": gender,
                "name": name,
                "email": email,
                "english_level": levels.find(x => x.id === level)?.title?.toUpperCase() ?? "BEGINNER",
                "how": hows,
                "why": why,
                "language": language,
                "practice_goal": "MINUTES_" + (goals.find(x => x.id === goal)?.title ?? "5"),
                "favorite_topics": topics,
                "struggles": struggles,
                "words_knows": vocabulary,
                "words_unknows": allWords.filter(x => !vocabulary.includes(x.id)).map(x => x.id)
            }),
            credentials: "include",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
        })
    }

    function _setGender(_gender: 'male' | 'female') {

    }

    function _setAge(_age: string) {

    }

    function _setSpeakingHarderThenUnderstand(value: boolean) {
        setSpeakingHarderThenUnderstand(value)
        localStorage.setItem("speakingHarderThenUnderstand", String(value))
    }

    if (!stateIsReady) {
        return <></>
    }

    return (
        <GlobalStateContext.Provider value={{
            language, setLanguage, topics, chooseTopic,
            currentStep, currentTitle: showTitle ? currentTitle : null, showLogo, showProgressBar, progressBarValue, buttonState, name,
            setName, isDarkView, why, setWhy, hows, chooseHow, level, setLevel, goal, setGoal, chooseStruggle, struggles,
            vocabulary, chooseVocabulary, skipVocabTest, email, setEmail, vocabResult, grammarResult,
            grammarAnswers, chooseGrammarAnswer, lexResults, recordLexResult, setProgressBarValue,
            setButtonActive: () => { setButtonState("active") },
            setButtonDisabled: () => { setButtonState("disabled") },
            finishOnboarding: sendOnboardingData, fromEmail,
            gender, setGender: _setGender,
            age, setAge: _setAge, stratedFromLanguage,
            speakingHarderThenUnderstand, setSpeakingHarderThenUnderstand: _setSpeakingHarderThenUnderstand,
            getResultOfVocabTest, STEPS, toggleTitle: setShowTitle, navigateToNextStep, isLangFromUserAgent
        }}>
            {children}
        </GlobalStateContext.Provider>
    )
}

export const useGlobalState = () => useContext(GlobalStateContext);